import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { SLIDE_META, SLIDE_ORDER, defaultSlide } from "@/lib/slides";
import { ResultView } from "@/components/ResultView";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import {
  ArrowLeft, Plus, Trash2, Save, Play, Loader2, GripVertical, X,
} from "lucide-react";

function TypeIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} />;
}

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slides, setSlides] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/presentations/${id}`);
        setTitle(data.title);
        setSlides(data.slides || []);
        if (!data.slides || data.slides.length === 0) setShowPicker(true);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
        navigate("/app");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/presentations/${id}`, { title, slides });
      if (!silent) toast.success("Saved");
      return true;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, slides]);

  const present = async () => {
    if (slides.length === 0) {
      toast.error("Add at least one slide first");
      return;
    }
    const ok = await save(true);
    if (!ok) return;
    try {
      const { data } = await api.post(`/presentations/${id}/sessions`);
      navigate(`/present/${data.code}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const addSlide = (type) => {
    const s = defaultSlide(type);
    const next = [...slides, s];
    setSlides(next);
    setActive(next.length - 1);
    setShowPicker(false);
  };

  const updateSlide = (patch) => {
    setSlides((prev) => prev.map((s, i) => (i === active ? { ...s, ...patch } : s)));
  };

  const removeSlide = (idx) => {
    const next = slides.filter((_, i) => i !== idx);
    setSlides(next);
    setActive(Math.max(0, Math.min(active, next.length - 1)));
  };

  const changeType = (newType) => {
    setSlides((prev) => prev.map((s, i) => {
      if (i !== active) return s;
      const base = defaultSlide(newType);
      return { ...base, id: s.id, question: s.question || base.question };
    }));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  }

  const slide = slides[active];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="presentation-editor">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="px-4 sm:px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/app")} className="p-2 rounded-lg hover:bg-slate-100 transition-all">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <input
            data-testid="presentation-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1 font-display font-bold text-lg text-slate-900 bg-transparent outline-none min-w-0"
            placeholder="Presentation title"
          />
          <button data-testid="save-button" onClick={() => save(false)} disabled={saving}
                  className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium rounded-xl px-4 py-2 border border-slate-200 transition-all">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <button data-testid="present-mode-button" onClick={present}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold rounded-xl px-5 py-2 shadow-lg shadow-indigo-500/25 transition-all hover:-translate-y-0.5">
            <Play className="w-4 h-4" /> Present
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left thumbnails */}
        <aside className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-3 space-y-2">
          {slides.map((s, i) => (
            <div key={s.id} onClick={() => setActive(i)} data-testid={`slide-thumb-${i}`}
                 className={`group relative rounded-xl border-2 p-3 cursor-pointer transition-all ${i === active ? "border-indigo-500 bg-indigo-50/50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono-code text-slate-400">{i + 1}</span>
                <TypeIcon name={SLIDE_META[s.type].icon} className="w-4 h-4" />
                <span className="text-xs font-semibold text-slate-500 truncate">{SLIDE_META[s.type].label}</span>
              </div>
              <p className="text-sm text-slate-800 mt-1 truncate">{s.question || "—"}</p>
              {slides.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); removeSlide(i); }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-all">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button data-testid="add-slide-button" onClick={() => setShowPicker(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:text-indigo-600 text-slate-500 rounded-xl py-3 text-sm font-medium transition-all">
            <Plus className="w-4 h-4" /> Add slide
          </button>
        </aside>

        {/* Center preview */}
        <main className="flex-1 overflow-y-auto p-6 sm:p-10 flex items-start justify-center">
          {slide ? (
            <div className="w-full max-w-3xl aspect-video bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex flex-col">
              <div className="text-xs font-semibold uppercase tracking-widest text-indigo-600 mb-3 flex items-center gap-1.5">
                <TypeIcon name={SLIDE_META[slide.type].icon} className="w-4 h-4" /> {SLIDE_META[slide.type].label}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-6">{slide.question || "Your question"}</h2>
              <div className="flex-1 overflow-hidden">
                <PreviewBody slide={slide} />
              </div>
            </div>
          ) : (
            <div className="text-center py-24">
              <p className="text-slate-500 text-lg mb-4">This presentation has no slides yet.</p>
              <button data-testid="empty-add-slide-button" onClick={() => setShowPicker(true)}
                      className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-5 py-2.5 transition-all">
                <Plus className="w-4 h-4" /> Choose a slide type
              </button>
            </div>
          )}
        </main>

        {/* Right config */}
        <aside className="w-80 shrink-0 border-l border-slate-200 bg-white overflow-y-auto p-5 space-y-5">
          {slide && <ConfigPanel slide={slide} update={updateSlide} onChangeType={changeType} />}
        </aside>
      </div>

      {/* Slide type picker */}
      {showPicker && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPicker(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Choose a slide type</h3>
              <button onClick={() => setShowPicker(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {SLIDE_ORDER.map((type) => (
                <button key={type} data-testid={`slide-type-${type.replace("_", "-")}`} onClick={() => addSlide(type)}
                        className="text-left p-4 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50/40 transition-all">
                  <TypeIcon name={SLIDE_META[type].icon} className="w-6 h-6 mb-2" style={{ color: SLIDE_META[type].color }} />
                  <div className="font-semibold text-slate-900 text-sm">{SLIDE_META[type].label}</div>
                  <div className="text-xs text-slate-400 mt-0.5 leading-snug">{SLIDE_META[type].desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewBody({ slide }) {
  if (slide.type === "quiz") {
    return (
      <div className="space-y-2">
        {slide.options.map((o, i) => (
          <div key={i} className={`px-4 py-2.5 rounded-xl border-2 font-medium flex items-center gap-2 ${i === slide.correct_option ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-700"}`}>
            {i === slide.correct_option && <span className="text-emerald-500">✓</span>}{o}
          </div>
        ))}
        <div className="text-xs text-slate-400 pt-1">⏱ {slide.time_limit}s to answer</div>
      </div>
    );
  }
  if (slide.type === "multiple_choice" || slide.type === "ranking") {
    return (
      <div className="space-y-2">
        {slide.options.map((o, i) => (
          <div key={i} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 font-medium">{o}</div>
        ))}
      </div>
    );
  }
  if (slide.type === "scales") {
    return (
      <div className="flex items-center justify-between text-slate-400">
        <span>{slide.scale_min_label} ({slide.scale_min})</span>
        <div className="flex-1 h-1 bg-slate-200 mx-4 rounded-full" />
        <span>{slide.scale_max_label} ({slide.scale_max})</span>
      </div>
    );
  }
  if (slide.type === "word_cloud") return <div className="text-slate-300 text-lg">Words will appear here</div>;
  if (slide.type === "open_ended") return <div className="text-slate-300 text-lg">Responses will appear as cards</div>;
  if (slide.type === "qa") return <div className="text-slate-300 text-lg">Questions with upvotes appear here</div>;
  return null;
}

function ConfigPanel({ slide, update, onChangeType }) {
  const setOption = (i, val) => {
    const opts = [...slide.options];
    opts[i] = val;
    update({ options: opts });
  };
  const addOption = () => update({ options: [...slide.options, `Option ${slide.options.length + 1}`] });
  const removeOption = (i) => update({ options: slide.options.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-indigo-600">Slide type</label>
        <select data-testid="change-slide-type" value={slide.type} onChange={(e) => onChangeType(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-slate-900 text-sm bg-white">
          {SLIDE_ORDER.map((t) => <option key={t} value={t}>{SLIDE_META[t].label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-indigo-600">Question</label>
        <textarea
          data-testid="slide-question-input"
          value={slide.question}
          onChange={(e) => update({ question: e.target.value })}
          rows={2}
          className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-slate-900 text-sm resize-none"
        />
      </div>

      {(slide.type === "multiple_choice" || slide.type === "ranking") && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-indigo-600">Options</label>
          <div className="mt-1.5 space-y-2">
            {slide.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                <input
                  data-testid={`option-input-${i}`}
                  value={o}
                  onChange={(e) => setOption(i, e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-500 outline-none text-sm text-slate-900 min-w-0"
                />
                {slide.options.length > 2 && (
                  <button onClick={() => removeOption(i)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button data-testid="add-option-button" onClick={addOption}
                    className="inline-flex items-center gap-1 text-sm text-indigo-600 font-medium hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add option
            </button>
          </div>
        </div>
      )}

      {slide.type === "scales" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Min</label>
              <input type="number" value={slide.scale_min} onChange={(e) => update({ scale_min: parseInt(e.target.value || "1", 10) })}
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Max</label>
              <input type="number" value={slide.scale_max} onChange={(e) => update({ scale_max: parseInt(e.target.value || "5", 10) })}
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Low label</label>
            <input value={slide.scale_min_label} onChange={(e) => update({ scale_min_label: e.target.value })}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">High label</label>
            <input value={slide.scale_max_label} onChange={(e) => update({ scale_max_label: e.target.value })}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm" />
          </div>
        </div>
      )}

      {slide.type === "quiz" && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-indigo-600">Answers · tap ✓ to mark correct</label>
            <div className="mt-1.5 space-y-2">
              {slide.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button type="button" onClick={() => update({ correct_option: i })} data-testid={`quiz-correct-${i}`}
                          className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${i === slide.correct_option ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-emerald-400"}`}>
                    ✓
                  </button>
                  <input
                    data-testid={`option-input-${i}`}
                    value={o}
                    onChange={(e) => setOption(i, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-500 outline-none text-sm text-slate-900 min-w-0"
                  />
                  {slide.options.length > 2 && (
                    <button onClick={() => { removeOption(i); if (slide.correct_option >= slide.options.length - 1) update({ correct_option: 0 }); }}
                            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {slide.options.length < 6 && (
                <button data-testid="add-option-button" onClick={addOption}
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 font-medium hover:underline">
                  <Plus className="w-3.5 h-3.5" /> Add answer
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Time limit (seconds)</label>
            <input type="number" min={5} max={120} value={slide.time_limit} data-testid="quiz-time-limit"
                   onChange={(e) => update({ time_limit: parseInt(e.target.value || "20", 10) })}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm" />
          </div>
        </div>
      )}

      {(slide.type === "word_cloud" || slide.type === "open_ended" || slide.type === "qa") && (
        <p className="text-sm text-slate-400 leading-relaxed">
          {SLIDE_META[slide.type].desc}. No extra configuration needed — audience responses stream in live.
        </p>
      )}
    </div>
  );
}
